import { MapLayer } from "./maps-base-layer";
import { getRasterTileUrl, isRasterTileLayerReady, safelyAddLayer, safelyAddSource, safelyRemoveLayer, safelyRemoveSource } from "./maps.utils";
import ProjectDownloadService from "../project-download.service";
import { store } from '../../store/stencil-redux';
import { getAppNetworkConnected } from "../../store/selectors";
import * as mapboxgl from 'mapbox-gl/esm';

export interface RasterTileLayerLayout {
  minZoom?: number;
  maxZoom?: number;
  tileSize?: number;
  /** `webp` for COG-backed layers; absent for legacy PNG pyramids */
  format?: string;
}

export class MapRasterTileLayer extends MapLayer {

  private currentTilesUrl: string | null = null;
  private onRasterLayerComplete?: (projectId: number, layerId: number) => void;
  private unsubscribeFromNetwork?: () => void;
  private lastNetworkConnected?: boolean;
  private pendingApply: Promise<void> = Promise.resolve();
  // Tracked so a layer hidden before its Mapbox layer exists is created hidden
  private visible = true;

  constructor(
    private source: string,
    private layout: RasterTileLayerLayout,
    private bounds: number[],
    private projectId: number,
    private layerId: number
  ) {
    super();
  }

  async addTo(map: mapboxgl.Map): Promise<void> {
    this.map = map;
    this.subscribeToDownload();
    this.subscribeToNetwork();
    await this.applySource();
  }

  remove(): void {
    this.unsubscribeFromDownload();
    this.unsubscribeFromNetwork?.();
    this.unsubscribeFromNetwork = undefined;
    this.lastNetworkConnected = undefined;
    this.currentTilesUrl = null;
    safelyRemoveLayer(this.map, this.id);
    safelyRemoveSource(this.map, this.id);
    this.map = undefined;
  }

  layerIds(): string[] {
    return [this.id];
  }

  show(): void {
    this.visible = true;
    super.show();
  }

  hide(): void {
    this.visible = false;
    super.hide();
  }

  private subscribeToDownload() {
    if (this.onRasterLayerComplete) {
      return;
    }
    this.onRasterLayerComplete = (projectId: number, layerId: number) => {
      if (projectId !== this.projectId || layerId !== this.layerId) {
        return;
      }
      void this.reapplySource();
    };
    ProjectDownloadService.instance.events.on('rasterLayerComplete', this.onRasterLayerComplete);
    ProjectDownloadService.instance.events.on('mapsComplete', this.handleMapsComplete);
  }

  private handleMapsComplete = (projectId: number) => {
    if (projectId !== this.projectId) {
      return;
    }
    void this.reapplySource();
  };

  private unsubscribeFromDownload() {
    if (this.onRasterLayerComplete) {
      ProjectDownloadService.instance.events.off('rasterLayerComplete', this.onRasterLayerComplete);
      this.onRasterLayerComplete = undefined;
    }
    ProjectDownloadService.instance.events.off('mapsComplete', this.handleMapsComplete);
  }

  /**
   * A native layer with no local pyramid cannot be shown while offline (applySource
   * bails out), and the download events alone will not fire until the pyramid is
   * complete. Re-apply the source when connectivity returns so the CDN template is
   * used in the meantime.
   */
  private subscribeToNetwork() {
    if (this.unsubscribeFromNetwork) {
      return;
    }
    this.lastNetworkConnected = getAppNetworkConnected(store.getState());
    this.unsubscribeFromNetwork = store.getStore().subscribe(() => {
      const connected = getAppNetworkConnected(store.getState());
      if (connected === this.lastNetworkConnected) {
        return;
      }
      this.lastNetworkConnected = connected;
      if (connected) {
        void this.reapplySource();
      }
    });
  }

  /**
   * Serialise source updates: download events and connectivity changes can fire close
   * together, and interleaved runs could otherwise overwrite a local template with
   * the CDN one.
   */
  private applySource(): Promise<boolean> {
    const run = this.pendingApply.then(() => this.applySourceNow());
    this.pendingApply = run.then(() => undefined, err => {
      console.warn(`Failed to apply raster tile source for layer ${this.layerId}:`, err);
    });
    return run;
  }

  /**
   * Re-apply after a download or connectivity event. If this created the Mapbox layer
   * (e.g. the layer was added offline and the device has reconnected), Mapbox appends
   * it on top of the stack, so ask the layer manager to restore the configured order.
   */
  private reapplySource(): Promise<void> {
    return this.applySource()
      .then(created => {
        if (created) {
          this.onLayersChanged?.();
        }
      })
      // applySource already logged the failure; event handlers must not leave an
      // unhandled rejection behind
      .catch(() => undefined);
  }

  /**
   * Resolves true when the Mapbox layer was created by this call (as opposed to
   * being absent or only having its tiles URL updated).
   */
  private async applySourceNow(): Promise<boolean> {
    if (!this.map) {
      return false;
    }

    const ready = await isRasterTileLayerReady(this.projectId, this.layerId);
    if (!this.map) {
      // remove() ran while we were awaiting
      return false;
    }
    const connected = getAppNetworkConnected(store.getState());

    if (!ready && !connected) {
      // Offline with no local tiles: nothing can be served yet. subscribeToNetwork
      // re-applies the source once the device reconnects.
      return false;
    }

    const tileUrlTemplate = await getRasterTileUrl(this.projectId, this.layerId, this.source, this.layout?.format);
    if (!this.map) {
      return false;
    }
    const existing = this.map.getSource(this.id);

    if (existing) {
      if (this.currentTilesUrl !== tileUrlTemplate && 'setTiles' in existing) {
        (existing as { setTiles: (tiles: string[]) => void }).setTiles([tileUrlTemplate]);
        this.currentTilesUrl = tileUrlTemplate;
      }
      return false;
    }

    safelyAddSource(this.map, this.id, {
      type: 'raster',
      tiles: [tileUrlTemplate],
      tileSize: this.layout?.tileSize || 256,
      minzoom: this.layout?.minZoom,
      maxzoom: this.layout?.maxZoom,
      bounds: this.bounds as [number, number, number, number]
    });

    safelyAddLayer(this.map, {
      id: this.id,
      type: 'raster',
      source: this.id,
      layout: {
        visibility: this.visible ? 'visible' : 'none'
      },
      paint: {
        'raster-fade-duration': 0
      }
    });
    this.currentTilesUrl = tileUrlTemplate;
    return true;
  }
}
